import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnalyticsTab } from '@/types/analytics.types';
import { useAnalyticsFilters } from './hooks/useAnalyticsFilters';
import {
  useAnalyticsSummary,
  useAnalyticsFundamentals,
  useAnalyticsHeatmap,
  useAnalyticsOna,
  useAnalyticsGenerational,
  useAnalyticsOmissions,
} from '@/hooks/useAnalytics';
import { USE_MOCK, type AnalyticsFilterParams } from '@/api/client';
import { PageSpinner } from '@/components/common/PageSpinner';
import { FilterBar } from './components/FilterBar';
import { ResumenTab } from './tabs/ResumenTab';
import { FundamentalsTab } from './tabs/FundamentalsTab';
import { HeatmapTab } from './tabs/HeatmapTab';
import { OnaTab } from './tabs/OnaTab';
import { GeneracionalTab } from './tabs/GeneracionalTab';
import './AnalyticsView.css';
import '@/components/common/OuTree/OuTreeCard.css';

const TABS: Array<{ id: AnalyticsTab; icon: string; labelKey: string }> = [
  { id: 'resumen',       icon: 'fa-chart-pie',    labelKey: 'analytics.tabs.summary'       },
  { id: 'fundamentals',  icon: 'fa-layer-group',  labelKey: 'analytics.tabs.fundamentals'  },
  { id: 'heatmap',       icon: 'fa-table-cells',  labelKey: 'analytics.tabs.heatmap'       },
  { id: 'red-org',       icon: 'fa-share-nodes',  labelKey: 'analytics.tabs.orgNetwork'    },
  { id: 'generacional',  icon: 'fa-people-group', labelKey: 'analytics.tabs.generational'  },
];

export function AnalyticsView() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('resumen');
  const { filters, setFilter, resetFilters } = useAnalyticsFilters();

  const apiFilters = useMemo<AnalyticsFilterParams>(() => ({
    ouId: filters.ou || undefined,
    startDate: filters.dateFrom || undefined,
    endDate: filters.dateTo || undefined,
    language: i18n.language.split('-')[0],
  }), [filters.ou, filters.dateFrom, filters.dateTo, i18n.language]);

  const { data: summaryData,      isLoading: loadingSummary }      = useAnalyticsSummary(apiFilters);
  const { data: fundamentalsData, isLoading: loadingFundamentals } = useAnalyticsFundamentals(apiFilters);
  const { data: heatmapData,      isLoading: loadingHeatmap }      = useAnalyticsHeatmap(apiFilters);
  const { data: onaData,          isLoading: loadingOna }          = useAnalyticsOna(apiFilters);
  const { data: generationalData, isLoading: loadingGenerational } = useAnalyticsGenerational(apiFilters);
  const { data: omissionsData,    isLoading: loadingOmissions }    = useAnalyticsOmissions(apiFilters);

  // En modo real los tabs reciben datos del servidor; en modo mock quedan undefined y cada
  // tab muestra su estado vacío explícito (sin fallback a mock — ver MOCK_FALLBACK.md).
  const apiSummary      = USE_MOCK ? undefined : summaryData;
  const apiFundamentals = USE_MOCK ? undefined : fundamentalsData;
  const apiHeatmap      = USE_MOCK ? undefined : heatmapData;
  const apiOna          = USE_MOCK ? undefined : onaData;
  const apiGenerational = USE_MOCK ? undefined : generationalData;
  const apiOmissions    = USE_MOCK ? undefined : omissionsData;

  return (
    <div className="au-shell">
      {/* Filter bar */}
      <FilterBar filters={filters} setFilter={setFilter} resetFilters={resetFilters} apiFundamentals={apiFundamentals} />

      {/* Tab row + search en la misma fila, search fuera del fondo de tabs */}
      <div className="au-tabs-outer">
        <div className="au-tabs-row">
          <div className="au-tabs-group" role="tablist" aria-label={t('analytics.title')}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`au-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`fas ${tab.icon}`} aria-hidden="true" />
                <span>{t(tab.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="au-search-wrap">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            className="au-search-input"
            placeholder={t('common.search') + '…'}
            value={filters.searchQuery}
            onChange={(e) => setFilter('searchQuery', e.target.value)}
            aria-label={t('common.search')}
          />
        </div>
      </div>

      {/* Tab panels */}
      <div
        className={`au-tab-panel${activeTab === 'resumen' ? ' active' : ''}`}
        role="tabpanel"
        aria-labelledby="tab-resumen"
      >
        {activeTab === 'resumen' && (
          (loadingSummary || loadingFundamentals)
            ? <PageSpinner />
            : <ResumenTab apiSummary={apiSummary} apiFundamentals={apiFundamentals} />
        )}
      </div>

      <div
        className={`au-tab-panel${activeTab === 'fundamentals' ? ' active' : ''}`}
        role="tabpanel"
        aria-labelledby="tab-fundamentals"
      >
        {activeTab === 'fundamentals' && (
          loadingFundamentals
            ? <PageSpinner />
            : <FundamentalsTab apiFundamentals={apiFundamentals} />
        )}
      </div>

      <div
        className={`au-tab-panel${activeTab === 'heatmap' ? ' active' : ''}`}
        role="tabpanel"
        aria-labelledby="tab-heatmap"
      >
        {activeTab === 'heatmap' && (
          (loadingHeatmap || loadingFundamentals || loadingSummary)
            ? <PageSpinner />
            : <HeatmapTab apiHeatmap={apiHeatmap} apiFundamentals={apiFundamentals} apiSummary={apiSummary} />
        )}
      </div>

      <div
        className={`au-tab-panel${activeTab === 'red-org' ? ' active' : ''}`}
        role="tabpanel"
        aria-labelledby="tab-red-org"
      >
        {activeTab === 'red-org' && (
          (loadingOna || loadingOmissions || loadingFundamentals || loadingHeatmap)
            ? <PageSpinner />
            : (
              <OnaTab
                apiOna={apiOna}
                apiOmissions={apiOmissions}
                apiFundamentals={apiFundamentals}
                apiHeatmap={apiHeatmap}
              />
            )
        )}
      </div>

      <div
        className={`au-tab-panel${activeTab === 'generacional' ? ' active' : ''}`}
        role="tabpanel"
        aria-labelledby="tab-generacional"
      >
        {activeTab === 'generacional' && (
          (loadingGenerational || loadingFundamentals)
            ? <PageSpinner />
            : (
              <GeneracionalTab
                apiGenerational={apiGenerational}
                apiFundamentals={apiFundamentals}
                filters={apiFilters}
              />
            )
        )}
      </div>
    </div>
  );
}
